/**
 * "Has a session" flag, kept in localStorage so the Navbar can decide whether
 * to pull in the firebase auth chunk at all. Anonymous visitors (most of our
 * traffic) never set it, so they never download auth + firestore just to
 * render a Login link.
 *
 * The flag is a hint, not the source of truth. Firebase's own persisted state
 * (indexedDB) remains authoritative; the flag only answers "is it worth
 * loading firebase to find out?". It is mirrored from real auth state in
 * src/firebase.js, so every sign-in path — Google popup/redirect, magic link,
 * OTP custom token, emulator test sign-in — sets it through one place, and a
 * signed-out or expired session clears it.
 *
 * Backward compatibility: users who signed in before this flag existed won't
 * have it, so on the homepage they see the logged-out navbar until they visit
 * any page that loads firebase (login, apply, the portal, an auth callback).
 * The mirror seeds the flag there, and future visits are correct. We accept
 * that one stale render instead of probing indexedDB up front, which would
 * cost async plumbing on every anonymous visit to speed up a one-time case.
 */

export const HAS_SESSION_KEY = "steelhacks:has-session";

/**
 * Last known role for the signed-in browser, so the Navbar can paint the
 * signed-in links on first render instead of after two async hops (firebase
 * restoring the session, then a Firestore read of users/{uid}). Same contract
 * as the flag above: a hint, never the source of truth. The real role always
 * arrives moments later and overwrites whatever this said.
 */
export const CACHED_ROLE_KEY = "steelhacks:nav-role";

// Only roles the Navbar actually branches on. Anything else — a typo, a role
// we retired, a value someone hand-edited into devtools — reads as unknown
// and the Navbar waits for Firestore, which is the pre-cache behaviour.
const KNOWN_ROLES = new Set(["staff", "judge", "sponsor", "user"]);

/**
 * Last known application status, cached for the same reason as the role: the
 * hacker portal decides whether to draw itself from the applicant's status,
 * and finding that out costs a session restore plus a Firestore lookup.
 */
export const CACHED_APP_STATUS_KEY = "steelhacks:portal-status";

const KNOWN_APP_STATUSES = new Set([
  "PENDING", "ACCEPTED", "REJECTED", "WAITLISTED", "NONE",
]);

/**
 * Last fetched Devpost/Discord portal links. They live in config/portalLinks
 * behind an accepted-hackers-only read rule, and fetching them is a second
 * Firestore round trip that only starts after the profile load finishes — so
 * without a cache they pop into the sidebar a beat after everything else.
 * Same contract as the other hints: render from cache instantly, let the
 * fresh read overwrite it moments later.
 */
export const CACHED_PORTAL_LINKS_KEY = "steelhacks:portal-links";

/** Fired on window when the flag is set, so an already-mounted Navbar (it
 * lives outside the router and never remounts) can start loading auth the
 * moment a sign-in completes in this tab. */
export const SESSION_FLAG_EVENT = "steelhacks:session-flag";

function defaultStorage() {
  // Accessing localStorage can itself throw (blocked storage, some privacy
  // modes); callers get null and each helper degrades gracefully.
  try {
    return typeof window !== "undefined" ? window.localStorage : null;
  } catch {
    return null;
  }
}

/**
 * Whether this browser has (or recently had) a signed-in session. Fails open:
 * if storage is unreadable we return true so the Navbar falls back to the old
 * always-load-auth behavior — correctness for signed-in users beats saving
 * bytes for the rare storage-blocked visitor.
 */
export function hasSessionFlag(storage = defaultStorage()) {
  if (!storage) return true;
  try {
    return storage.getItem(HAS_SESSION_KEY) === "1";
  } catch {
    return true;
  }
}

/**
 * Record that a session exists and announce it to this tab. The event fires
 * even when the write fails so the in-page Navbar still updates; the flag
 * write is only about the next page load.
 */
export function markSessionFlag(storage = defaultStorage()) {
  try {
    storage?.setItem(HAS_SESSION_KEY, "1");
  } catch {
    // Ignore: worst case the next visit re-loads auth unconditionally.
  }
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event(SESSION_FLAG_EVENT));
  }
}

/** Forget the session hint (sign-out, or firebase reports no user). */
export function clearSessionFlag(storage = defaultStorage()) {
  try {
    storage?.removeItem(HAS_SESSION_KEY);
    // The cached role is only meaningful while a session hint exists, so it
    // dies here rather than at each of the call sites that clear the flag.
    storage?.removeItem(CACHED_ROLE_KEY);
    storage?.removeItem(CACHED_APP_STATUS_KEY);
    // The portal links are gated content (the Discord invite especially);
    // a cached copy must not outlive the session that was allowed to read it.
    storage?.removeItem(CACHED_PORTAL_LINKS_KEY);
  } catch {
    // Ignore: an unclearable flag only costs one wasted auth load.
  }
}

/**
 * The cached role, or null when there isn't a usable one. Returns null with no
 * session hint: a role without a session is a leftover, and trusting it would
 * paint signed-in links for someone who is signed out.
 * @param {Storage} [storage] Injected for tests.
 * @return {string|null} A role the Navbar knows how to render, or null.
 */
export function readCachedRole(storage = defaultStorage()) {
  if (!storage) return null;
  try {
    if (storage.getItem(HAS_SESSION_KEY) !== "1") return null;
    const role = storage.getItem(CACHED_ROLE_KEY);
    return KNOWN_ROLES.has(role) ? role : null;
  } catch {
    return null;
  }
}

/**
 * Remember the role for the next page load. Unknown roles clear the entry
 * instead of storing it, so a role we stop recognising can't linger.
 * @param {string} role Role as reported by Firestore.
 * @param {Storage} [storage] Injected for tests.
 */
export function writeCachedRole(role, storage = defaultStorage()) {
  try {
    if (KNOWN_ROLES.has(role)) storage?.setItem(CACHED_ROLE_KEY, role);
    else storage?.removeItem(CACHED_ROLE_KEY);
  } catch {
    // Ignore: worst case the next visit paints logged-out first, as before.
  }
}

/**
 * The cached application status, or null when there isn't a usable one. Same
 * session-flag guard as readCachedRole: a status without a session is a
 * leftover from whoever was signed in before.
 * @param {Storage} [storage] Injected for tests.
 * @return {string|null} A known status, or null.
 */
export function readCachedAppStatus(storage = defaultStorage()) {
  if (!storage) return null;
  try {
    if (storage.getItem(HAS_SESSION_KEY) !== "1") return null;
    const status = storage.getItem(CACHED_APP_STATUS_KEY);
    return KNOWN_APP_STATUSES.has(status) ? status : null;
  } catch {
    return null;
  }
}

/**
 * Remember the application status for the next page load.
 * @param {string} status Status as reported by Firestore.
 * @param {Storage} [storage] Injected for tests.
 */
export function writeCachedAppStatus(status, storage = defaultStorage()) {
  try {
    if (KNOWN_APP_STATUSES.has(status)) storage?.setItem(CACHED_APP_STATUS_KEY, status);
    else storage?.removeItem(CACHED_APP_STATUS_KEY);
  } catch {
    // Ignore: worst case the portal shows its spinner one more time.
  }
}

/**
 * The cached portal links, or null when there isn't a usable copy. Only the
 * string URL fields the sidebar actually reads survive the round trip, so a
 * hand-edited or stale-shaped entry degrades to "wait for Firestore" rather
 * than rendering junk. Session-flag guarded like the other hints.
 * @param {Storage} [storage] Injected for tests.
 * @return {{devpostUrl?: string, discordUrl?: string}|null} Cached links.
 */
export function readCachedPortalLinks(storage = defaultStorage()) {
  if (!storage) return null;
  try {
    if (storage.getItem(HAS_SESSION_KEY) !== "1") return null;
    const parsed = JSON.parse(storage.getItem(CACHED_PORTAL_LINKS_KEY));
    const links = {};
    if (typeof parsed?.devpostUrl === "string") links.devpostUrl = parsed.devpostUrl;
    if (typeof parsed?.discordUrl === "string") links.discordUrl = parsed.discordUrl;
    return Object.keys(links).length > 0 ? links : null;
  } catch {
    return null;
  }
}

/**
 * Remember the portal links for the next page load. Stores only the fields
 * the sidebar renders; an empty or malformed doc clears the entry.
 * @param {object} links The config/portalLinks document data.
 * @param {Storage} [storage] Injected for tests.
 */
export function writeCachedPortalLinks(links, storage = defaultStorage()) {
  try {
    const keep = {};
    if (typeof links?.devpostUrl === "string") keep.devpostUrl = links.devpostUrl;
    if (typeof links?.discordUrl === "string") keep.discordUrl = links.discordUrl;
    if (Object.keys(keep).length > 0) {
      storage?.setItem(CACHED_PORTAL_LINKS_KEY, JSON.stringify(keep));
    } else {
      storage?.removeItem(CACHED_PORTAL_LINKS_KEY);
    }
  } catch {
    // Ignore: worst case the links pop in a beat late, as before.
  }
}
